import React, { useRef, useCallback, useState, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { eventBus, EVENTS } from '../../../../../utils/EventBus'
import { getKeyboardNavigationDirection } from '../utils/keyboardNavigation'

const findNextInput = (currentInput, direction) => {
  const currentTd = currentInput.closest('td, th')
  if (!currentTd) return null
  const currentTr = currentTd.closest('tr')
  if (!currentTr) return null

  const allTrs = Array.from(document.querySelectorAll('.rp-table tr'))
  const currentTrIdx = allTrs.indexOf(currentTr)
  const isAutocomplete = (el) => el.closest('.MuiAutocomplete-root')

  if (direction === 'up' || direction === 'down') {
    const step = direction === 'up' ? -1 : 1
    const currentRect = currentTd.getBoundingClientRect()
    const currentCenter = currentRect.left + currentRect.width / 2

    for (let i = currentTrIdx + step; i >= 0 && i < allTrs.length; i += step) {
      const cells = Array.from(allTrs[i].cells)
      const targetTd = cells.find((td) => {
        const rect = td.getBoundingClientRect()
        return currentCenter >= rect.left && currentCenter <= rect.right
      })
      if (targetTd) {
        const input = targetTd.querySelector('input')
        if (
          input &&
          !input.disabled &&
          !input.readOnly &&
          !isAutocomplete(input)
        ) {
          return input
        }
      }
    }
  } else if (direction === 'left' || direction === 'right') {
    const allInputs = Array.from(
      document.querySelectorAll('.rp-table input')
    ).filter((i) => !i.disabled && !i.readOnly && !isAutocomplete(i))

    const currentIndex = allInputs.indexOf(currentInput)
    if (direction === 'left' && currentIndex > 0)
      return allInputs[currentIndex - 1]
    if (direction === 'right' && currentIndex < allInputs.length - 1)
      return allInputs[currentIndex + 1]
  }
  return null
}

const ensureVisible = (element) => {
  const scrollContainer = element.closest('.rp-table-scroll')
  if (!scrollContainer) return
  const rect = element.getBoundingClientRect()
  const containerRect = scrollContainer.getBoundingClientRect()
  const stickyCols = Array.from(scrollContainer.querySelectorAll('.sticky-col'))
  let stickyWidth = 0
  if (stickyCols.length > 0) {
    const lastSticky = stickyCols[stickyCols.length - 1]
    stickyWidth = lastSticky.getBoundingClientRect().right - containerRect.left
  }
  if (rect.left < containerRect.left + stickyWidth) {
    scrollContainer.scrollLeft -=
      containerRect.left + stickyWidth - rect.left + 20
  } else if (rect.right > containerRect.right) {
    scrollContainer.scrollLeft += rect.right - containerRect.right + 20
  }

  const thead = scrollContainer.querySelector('thead')
  const headerHeight = thead ? thead.getBoundingClientRect().height : 0

  if (rect.top < containerRect.top + headerHeight) {
    scrollContainer.scrollTop -= (containerRect.top + headerHeight - rect.top + 20)
  } else if (rect.bottom > containerRect.bottom) {
    scrollContainer.scrollTop += (rect.bottom - containerRect.bottom + 20)
  }
}

const CreatableSelectCell = ({
  item,
  field,
  value,
  onChange,
  options = [],
  validateValue,
  distributionManager,
  rowSpan,
  cellStyle = {},
  normalizeOnCommit,
  readOnly = false
}) => {
  const inputRef = useRef(null)

  const isCommittingRef = useRef(false)
  const isDirtyRef = useRef(false)
  const selectingRef = useRef(false)
  const dropdownRef = useRef(null)
  // Track whether focus was triggered by mouse click (to open dropdown) or keyboard (Cell Mode)
  const focusedByMouseRef = useRef(false)
  const controlKeyRef = useRef({ pending: false, combined: false })

  const [localValue, setLocalValue] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIdx, setHighlightedIdx] = useState(-1)
  const [dropdownPos, setDropdownPos] = useState(null)

  const getOptionValue = useCallback((opt) => {
    if (opt && typeof opt === 'object')
      return String(opt.name ?? opt.value ?? '')
    return String(opt ?? '')
  }, [])

  const getOptionLabel = useCallback((opt) => {
    if (opt && typeof opt === 'object') {
      return `${opt.item_no || ''} ${opt.name || opt.value || ''}`.trim()
    }
    return String(opt ?? '')
  }, [])

  /**
   * Get value from distribution manager
   */
  const getDisplayValue = useCallback(() => {
    if (distributionManager && item.hinmeiCode && item.niukabango) {
      const fixed = distributionManager.getFixedField(
        item.hinmeiCode,
        item.niukabango,
        field,
        value
      )

      return fixed ?? value ?? ''
    }

    return value ?? ''
  }, [distributionManager, item.hinmeiCode, item.niukabango, field, value])

  /**
   * Sync external value
   */
  useEffect(() => {
    if (isCommittingRef.current) return

    setLocalValue(getDisplayValue())
  }, [getDisplayValue])

  /**
   * Distribution changed
   */
  useEffect(() => {
    return eventBus.on(EVENTS.DISTRIBUTION_CHANGED, (data) => {
      if (
        data.hinmeiCode === item.hinmeiCode &&
        String(data.niukabango) === String(item.niukabango) &&
        data.field === field &&
        data.type === 'fixed'
      ) {
        if (!isCommittingRef.current) {
          setLocalValue(data.value ?? '')
        }
      }
    })
  }, [item.hinmeiCode, item.niukabango, field])

  /**
   * Reset event
   */
  useEffect(() => {
    return eventBus.on(EVENTS.DISTRIBUTION_RESET, () => {
      if (!isCommittingRef.current) {
        setLocalValue(getDisplayValue())
      }
    })
  }, [getDisplayValue])

  /**
   * Filter options
   */
  const filteredOptions = useMemo(() => {
    const keyword = String(localValue ?? '').toLowerCase()
    return options.filter((opt) => {
      const value = getOptionValue(opt).toLowerCase()
      const label = getOptionLabel(opt).toLowerCase()
      return value.includes(keyword) || label.includes(keyword)
    })
  }, [options, localValue, getOptionValue, getOptionLabel])

  /**
   * Detect new value
   */
  const isNewValue = useMemo(() => {
    if (!localValue) return false

    return !options.some(
      (o) =>
        getOptionValue(o).toLowerCase() === String(localValue).toLowerCase()
    )
  }, [options, localValue, getOptionValue])

  /**
   * Update dropdown position
   */
  const updateDropdownPosition = useCallback(() => {
    if (!inputRef.current) return

    const rect = inputRef.current.getBoundingClientRect()
    const windowHeight = window.innerHeight
    const spaceBelow = windowHeight - rect.bottom
    const spaceAbove = rect.top
    const dropdownHeight = 250 // Estimated max height

    let top = rect.bottom + 4
    let bottom = 'auto'
    let maxHeight = 250

    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      maxHeight = Math.min(spaceAbove - 10, dropdownHeight)
      top = 'auto'
      bottom = windowHeight - rect.top + 4
    } else {
      maxHeight = Math.min(spaceBelow - 10, dropdownHeight)
    }

    setDropdownPos({
      top,
      bottom,
      left: rect.left,
      width: Math.max(rect.width, 180),
      maxHeight
    })
  }, [])

  /**
   * Commit value
   */
  const doCommit = useCallback(
    (val) => {
      if (readOnly) return true
      if (!isDirtyRef.current) return true
      if (validateValue && !validateValue(val)) {
        setIsOpen(false)
        return false
      }

      const commitValue = normalizeOnCommit ? normalizeOnCommit(val) : val
      if (validateValue && !validateValue(commitValue)) {
        setIsOpen(false)
        return false
      }

      isCommittingRef.current = true
      isDirtyRef.current = false

      setLocalValue(commitValue)
      onChange(item, field, commitValue)

      requestAnimationFrame(() => {
        isCommittingRef.current = false
      })
      return true
    },
    [item, field, onChange, validateValue, normalizeOnCommit, readOnly]
  )

  /**
   * Commit selected option — always focuses back to current cell (no navigate after)
   */
  const commitOption = useCallback(
    (opt) => {
      if (readOnly) return false
      const optionValue = getOptionValue(opt)
      if (validateValue && !validateValue(optionValue)) {
        setIsOpen(false)
        selectingRef.current = false
        return false
      }

      const commitValue = normalizeOnCommit
        ? normalizeOnCommit(optionValue)
        : optionValue
      if (validateValue && !validateValue(commitValue)) {
        setIsOpen(false)
        selectingRef.current = false
        return false
      }

      setLocalValue(commitValue)
      setIsOpen(false)

      isCommittingRef.current = true
      isDirtyRef.current = false

      onChange(item, field, commitValue)

      requestAnimationFrame(() => {
        isCommittingRef.current = false
        selectingRef.current = false
        // Focus back to current cell after committing
        inputRef.current?.focus()
      })
      return true
    },
    [
      item,
      field,
      onChange,
      getOptionValue,
      validateValue,
      normalizeOnCommit,
      readOnly
    ]
  )

  /**
   * Focus — only open dropdown when triggered by mouse click
   */
  const handleFocus = useCallback(() => {
    if (readOnly) return
    updateDropdownPosition()

    if (focusedByMouseRef.current) {
      // Mouse click → open dropdown immediately (existing behavior)
      setIsOpen(true)
      setHighlightedIdx(-1)
      focusedByMouseRef.current = false
    }
    // Keyboard focus → Cell Mode (dropdown stays closed)

    eventBus.emit(EVENTS.CELL_FOCUSED, {
      hinmeiCode: item.hinmeiCode,
      niukabango: item.niukabango,
      field
    })
  }, [updateDropdownPosition, item.hinmeiCode, item.niukabango, field, readOnly])

  /**
   * Input change
   */
  const handleChange = useCallback(
    (e) => {
      if (readOnly) return
      const newValue = e.target.value
      if (validateValue && !validateValue(newValue)) return

      setLocalValue(newValue)

      isDirtyRef.current = true

      setIsOpen(true)
      setHighlightedIdx(-1)
    },
    [validateValue, readOnly]
  )

  /**
   * Blur
   */
  const handleBlur = useCallback(
    (e) => {
      controlKeyRef.current = { pending: false, combined: false }
      if (readOnly) return
      if (selectingRef.current) return

      setIsOpen(false)

      doCommit(e.target.value)
    },
    [doCommit, readOnly]
  )

  /**
   * Keyboard navigation — Cell Mode vs Dropdown Mode
   */
  const handleKeyDown = useCallback(
    (e) => {
      if (readOnly) return
      if (e.key === 'Control') {
        controlKeyRef.current = { pending: true, combined: false }
        return
      }
      if (e.ctrlKey || controlKeyRef.current.pending) {
        if (controlKeyRef.current.pending) {
          controlKeyRef.current.combined = true
        }
        return
      }
      const keyboardDirection = getKeyboardNavigationDirection(e.key)
      // ── CELL MODE (dropdown is closed) ──────────────────────────────
      if (!isOpen) {
        if (keyboardDirection) {
          // Commit in Cell Mode, then navigate according to the shared key map.
          e.preventDefault()
          if (doCommit(localValue)) {
            setIsOpen(false)
            const nextInput = findNextInput(e.target, keyboardDirection)
            if (nextInput) {
              nextInput.focus()
              nextInput.select()
              ensureVisible(nextInput)
            }
          }
          return
        }

        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
          const isLeft = e.key === 'ArrowLeft'
          const isRight = e.key === 'ArrowRight'
          if (isLeft && e.target.selectionStart !== 0) return
          if (isRight && e.target.selectionEnd !== e.target.value.length) return

          const direction =
            e.key === 'ArrowUp' ? 'up' :
            e.key === 'ArrowDown' ? 'down' :
            e.key === 'ArrowLeft' ? 'left' : 'right'

          const nextInput = findNextInput(e.target, direction)
          if (nextInput) {
            e.preventDefault()
            nextInput.focus()
            nextInput.select()
            ensureVisible(nextInput)
          }
          return
        }

        if (e.key === 'Escape') {
          e.preventDefault()
          isDirtyRef.current = false
          setLocalValue(getDisplayValue())
          inputRef.current?.blur()
          return
        }

        return
      }

      // ── DROPDOWN MODE (dropdown is open) ────────────────────────────
      if (keyboardDirection && e.key !== 'Enter') {
        e.preventDefault()
        if (doCommit(localValue)) {
          setIsOpen(false)
          const nextInput = findNextInput(e.target, keyboardDirection)
          if (nextInput) {
            nextInput.focus()
            nextInput.select()
            ensureVisible(nextInput)
          }
        }
        return
      }

      if (e.key === 'ArrowDown') {
        // Navigate down in options — do NOT navigate table rows
        e.preventDefault()
        const max = filteredOptions.length + (isNewValue ? 0 : -1)
        setHighlightedIdx((prev) => Math.min(prev + 1, max))

      } else if (e.key === 'ArrowUp') {
        // Navigate up in options — do NOT navigate table rows
        e.preventDefault()
        setHighlightedIdx((prev) => Math.max(prev - 1, -1))

      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        // Dismiss dropdown — close, keep unsaved filter text, stay on current cell
        e.preventDefault()
        setIsOpen(false)
        // Focus stays on current input (no table navigation)
        inputRef.current?.focus()

      } else if (e.key === 'Enter') {
        e.preventDefault()

        const allOptions = isNewValue
          ? [...filteredOptions, localValue]
          : filteredOptions

        if (highlightedIdx >= 0 && allOptions[highlightedIdx] != null) {
          // Commit highlighted option → focus back to current cell
          commitOption(allOptions[highlightedIdx])
        } else {
          // No option highlighted → commit typed value, close dropdown, focus back to current cell
          if (doCommit(localValue)) {
            setIsOpen(false)
            requestAnimationFrame(() => {
              inputRef.current?.focus()
            })
          }
        }

      } else if (e.key === 'Escape') {
        e.preventDefault()
        isDirtyRef.current = false
        setLocalValue(getDisplayValue())
        setIsOpen(false)
        inputRef.current?.focus()
      }
    },
    [
      isOpen,
      filteredOptions,
      highlightedIdx,
      isNewValue,
      localValue,
      doCommit,
      commitOption,
      getDisplayValue,
      updateDropdownPosition,
      readOnly
    ]
  )

  const handleKeyUp = useCallback(
    (e) => {
      if (e.key !== 'Control') return
      const controlState = controlKeyRef.current
      controlKeyRef.current = { pending: false, combined: false }
      if (readOnly || !controlState.pending || controlState.combined) return

      e.preventDefault()
      if (doCommit(localValue)) {
        setIsOpen(false)
        const nextInput = findNextInput(e.currentTarget, 'left')
        if (nextInput) {
          nextInput.focus()
          nextInput.select()
          ensureVisible(nextInput)
        }
      }
    },
    [doCommit, ensureVisible, localValue, readOnly]
  )

  useEffect(() => {
    if (!isOpen) return

    if (highlightedIdx < 0) return

    const activeEl = dropdownRef.current?.querySelector(
      `[data-option-index="${highlightedIdx}"]`
    )

    activeEl?.scrollIntoView({
      block: 'nearest'
    })
  }, [highlightedIdx, isOpen])

  /**
   * Recalculate dropdown position on scroll/resize
   */
  useEffect(() => {
    if (!isOpen) return

    window.addEventListener('scroll', updateDropdownPosition, true)

    window.addEventListener('resize', updateDropdownPosition)

    return () => {
      window.removeEventListener('scroll', updateDropdownPosition, true)

      window.removeEventListener('resize', updateDropdownPosition)
    }
  }, [isOpen, updateDropdownPosition])

  return (
    <td
      className='data-cell sticky-col text-[10px] px-0'
      style={{
        textAlign: 'center',
        zIndex: 2,
        ...cellStyle
      }}
      rowSpan={rowSpan}
    >
      <input
        ref={inputRef}
        type='text'
        value={localValue}
        placeholder='-'
        title={readOnly ? '' : 'クリックして編集'}
        className='rp-cell-input text-[12px] py-2'
        readOnly={readOnly}
        onMouseDown={() => {
          if (!readOnly) focusedByMouseRef.current = true
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
      />

      {isOpen &&
        dropdownPos &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: 'fixed',
              top: dropdownPos.top,
              bottom: dropdownPos.bottom,
              left: dropdownPos.left,
              width: dropdownPos.width,
              maxHeight: dropdownPos.maxHeight,
              overflowY: 'auto',
              zIndex: 999999
            }}
            className='rp-cell-options'
            onMouseDown={(e) => {
              e.preventDefault()
              selectingRef.current = true
            }}
          >
            {filteredOptions.length === 0 && !isNewValue && (
              <div className='rp-cell-option text-gray-400'>オプションなし</div>
            )}

            {filteredOptions.map((opt, idx) => (
              <div
                key={`${getOptionValue(opt)}-${idx}`}
                data-option-index={idx}
                className={`rp-cell-option ${
                  idx === highlightedIdx ? 'bg-blue-100' : ''
                }`}
                onClick={() => commitOption(opt)}
              >
                {getOptionLabel(opt)}
              </div>
            ))}

            {isNewValue && (
              <div
                className={`rp-cell-option border-t border-gray-100 text-blue-600 ${
                  highlightedIdx === filteredOptions.length ? 'bg-blue-50' : ''
                }`}
                onClick={() => commitOption(localValue)}
              >
                + "{localValue}" を追加
              </div>
            )}
          </div>,
          document.body
        )}
    </td>
  )
}

export default React.memo(CreatableSelectCell)
